import React, { useEffect, useRef, Fragment, Component } from 'react';
import { CheckCircle, AlertCircle, User, Briefcase, Users, Shield, Heart, Gift, Star, DollarSign, Home } from 'lucide-react';
type MilestoneStatus = 'locked' | 'available' | 'in_progress' | 'completed' | 'skipped' | 'needs_attention' | 'flagged';
interface MilestoneDefinition {
  id: string;
  label: string;
  shortLabel: string;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface PhaseStripProps {
  currentMilestoneId: string;
  milestoneStatuses: Record<string, MilestoneStatus>;
  overallPercentage: number;
}
const MILESTONES: MilestoneDefinition[] = [{
  id: 'about',
  label: 'About You',
  shortLabel: 'About',
  icon: User
}, {
  id: 'background',
  label: 'Background',
  shortLabel: 'Background',
  icon: Briefcase
}, {
  id: 'family',
  label: 'Family',
  shortLabel: 'Family',
  icon: Users
}, {
  id: 'executor',
  label: 'Executors',
  shortLabel: 'Executors',
  icon: Shield
}, {
  id: 'funeral',
  label: 'Funeral',
  shortLabel: 'Funeral',
  icon: Heart
}, {
  id: 'personalGifts',
  label: 'Personal Gifts',
  shortLabel: 'Gifts',
  icon: Gift
}, {
  id: 'specialAssets',
  label: 'Special Assets',
  shortLabel: 'Assets',
  icon: Star
}, {
  id: 'financialGifts',
  label: 'Financial',
  shortLabel: 'Financial',
  icon: DollarSign
}, {
  id: 'realEstate',
  label: 'Real Estate',
  shortLabel: 'Property',
  icon: Home
}, {
  id: 'review',
  label: 'Review',
  shortLabel: 'Review',
  icon: CheckCircle
}];
function CircularProgress({
  percentage
}: {
  percentage: number;
}) {
  const size = 44;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - percentage / 100 * circumference;
  return <div className="relative flex items-center justify-center flex-shrink-0" style={{
    width: size,
    height: size
  }} data-id="element-1043">
      <svg width={size} height={size} className="-rotate-90" data-id="element-1044">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E8EEF4" strokeWidth={strokeWidth} data-id="element-1045" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#7854FF" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-700 ease-out" data-id="element-1046" />
      </svg>
      <span className="absolute text-xs font-bold text-primary" data-id="element-1047">
        {percentage}%
      </span>
    </div>;
}
export function PhaseStrip({
  currentMilestoneId,
  milestoneStatuses,
  overallPercentage
}: PhaseStripProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeIndex = MILESTONES.findIndex(m => m.id === currentMilestoneId);
  // Auto-scroll to active milestone
  useEffect(() => {
    if (containerRef.current && activeIndex >= 0) {
      const children = containerRef.current.children;
      // Each milestone has a pill + connector, so the pill is at index * 2
      const activeElement = children[activeIndex * 2] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeIndex]);
  return <div className="w-full" data-id="element-1048">
      {/* Milestone pills */}
      <div ref={containerRef} className="overflow-x-auto px-4 py-2.5 no-scrollbar" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-1049">
        <div className="flex items-center gap-1.5 sm:gap-2 w-max mx-auto" data-id="element-1050">
          {MILESTONES.map((milestone, index) => {
          const status = milestoneStatuses[milestone.id];
          const isCurrent = milestone.id === currentMilestoneId;
          const isCompleted = status === 'completed' || status === 'skipped';
          const isFlagged = status === 'flagged' || status === 'needs_attention';
          const Icon = milestone.icon;
          return <Fragment key={milestone.id} data-id="element-1051">
                <div className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${isCurrent ? 'bg-violetBlue text-white shadow-sm' : isCompleted ? 'bg-white text-primary border border-borderColor' : isFlagged ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-100 text-secondary'}`} data-id="element-1052">
                  {isCompleted ? <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-turquoise" data-id="element-1053" /> : isFlagged ? <AlertCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-1054" /> : isCurrent ? <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-1055" /> : null}
                  <span className="sm:hidden" data-id="element-1056">{milestone.shortLabel}</span>
                  <span className="hidden sm:inline" data-id="element-1057">{milestone.label}</span>
                </div>
                {index < MILESTONES.length - 1 && <div className={`w-2 sm:w-3 lg:w-5 h-[1.5px] flex-shrink-0 transition-colors ${isCompleted ? 'bg-turquoise' : 'bg-borderColor'}`} data-id="element-1058" />}
              </Fragment>;
        })}

          {/* Circular Progress */}
          <div className="ml-1 sm:ml-2" data-id="element-1059">
            <CircularProgress percentage={overallPercentage} data-id="element-1060" />
          </div>
        </div>
      </div>
    </div>;
}